import React from 'react'
import cx from 'classnames'
import { t } from 'ttag'

import { Box } from 'components/layout/Box/Box'
import { Form } from 'components/shared/Form/Form'
import {
  Icon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'components/shared/Icon/Icon'

import { ASSESSMENT_GROUP_FIELD, SCORE_FIELD, SKILL_RANGE } from '../../model'

import type { InterviewSection } from '../../types'

import styles from './SummaryAssessmentForm.module.scss'

type SummaryAssessmentFormProps = {
  className?: string
  control
  fields: Record<'id', string>[]
  skillTitles: Record<string, string>
  watch: (name: string) => unknown
  sections?: InterviewSection[]
  questionComments?: Record<string, Record<string, string>>
  onCommentChange?: (
    sectionKey: string,
    questionId: string,
    value: string,
  ) => void
}

const getSectionByKey = (sections: InterviewSection[], key: string) =>
  (sections ?? []).find(section => section?.key === key)

export const SummaryAssessmentForm = ({
  className = undefined,
  control,
  fields,
  skillTitles,
  watch,
  sections = [],
  questionComments = {},
  onCommentChange,
}: SummaryAssessmentFormProps) => {
  const assessmentFields = fields as (Record<'id', string> & { key: string })[]
  const length = fields.length
  const [expandedNotes, setExpandedNotes] = React.useState<
    Record<string, boolean>
  >({})

  const handleNotesToggle = React.useCallback(
    sectionKey => () => {
      setExpandedNotes(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))
    },
    [],
  )

  const handleNotesChange = React.useCallback(
    (sectionKey, questionId) => e => {
      onCommentChange?.(sectionKey, questionId, e.target.value)
    },
    [onCommentChange],
  )

  return (
    <Form
      className={cx(styles.assessment, {
        [className ?? '']: Boolean(className),
      })}
    >
      {assessmentFields.map(({ id, key }, index) => {
        const count = index + 1
        const section = getSectionByKey(sections, key)
        const questions = (section?.questions ?? []).filter(q => q.id != null)

        const score = watch(
          `${ASSESSMENT_GROUP_FIELD}[${index}].${SCORE_FIELD}`,
        )

        return (
          <Box className={styles.content} direction="column" key={id}>
            <span className={styles.count}>{t`${count} of ${length}`}</span>
            <h3 className={styles.title}>{skillTitles[key]}</h3>
            <Box align="center" className={styles.score} justify="center">
              {Array.from(
                { length: SKILL_RANGE },
                (_, scoreIndex) => scoreIndex + 1,
              ).map(value => (
                <Form.Field
                  className={styles.field}
                  id={`${id}-score-${value}`}
                  field="score"
                  key={value}
                  label={value}
                  labelSize="medium"
                  name={`${ASSESSMENT_GROUP_FIELD}[${index}].${SCORE_FIELD}`}
                  score={value}
                  isScoreActive={Number(score) === value}
                  {...{ control }}
                />
              ))}
            </Box>
            {questions.length > 0 ? (
              <Box className={styles.notesDropdown} direction="column">
                <button
                  type="button"
                  className={styles.notesTrigger}
                  onClick={handleNotesToggle(key)}
                  aria-expanded={expandedNotes[key]}
                >
                  <span className={styles.notesTriggerLabel}>{t`Notes`}</span>
                  <Icon
                    icon={expandedNotes[key] ? ChevronUpIcon : ChevronDownIcon}
                    size={20}
                  />
                </button>
                {expandedNotes[key] ? (
                  <Box className={styles.notesContent} direction="column">
                    {questions.map(
                      ({ id: questionId, title: questionTitle }) => {
                        const noteKey =
                          questionId != null ? String(questionId) : null
                        if (!noteKey) return null
                        const noteValue =
                          questionComments?.[key]?.[noteKey] ?? ''
                        return (
                          <Box
                            key={noteKey}
                            className={styles.notesSection}
                            direction="column"
                          >
                            <label
                              className={styles.notesLabel}
                              htmlFor={`notes-${noteKey}`}
                            >
                              {questionTitle}
                            </label>
                            <textarea
                              id={`notes-${noteKey}`}
                              className={styles.notesInput}
                              value={noteValue}
                              onChange={
                                onCommentChange
                                  ? handleNotesChange(key, noteKey)
                                  : undefined
                              }
                              readOnly={!onCommentChange}
                              rows={2}
                              placeholder={t`Add your notes…`}
                            />
                          </Box>
                        )
                      },
                    )}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        )
      })}
    </Form>
  )
}
